import { useMemo } from 'react'
import { Button } from '@/atoms/Button'
import { TitleMD, TitleSM } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import { Input, TextArea, Select } from '../../components/Inputs'
import { Page } from '../../components/Page'
import { PageSection } from '../../components/PageSection'
import {
  TorchApplicationData,
  TorchApplicationGenre,
  TorchApplicationTypes,
  useTorchApplication,
} from '../../components/TorchApplicationContext'
import { jotformCountries } from '../Agreement/jotFormCountries'
import { PresentationTypes } from './PresentationTypes'
import { ProductionTypes } from './ProductionTypes'
import { TorchAcknowledgement } from './TorchAcknowledgement'
import { TorchAges } from './TorchAges'
import { TorchGenres } from './TorchGenres'
import { TorchProduction } from './TorchProduction'

const requiredFields = [
  'title',
  'description',
  'productionType',
  'presentationType',
  'genres',
  'productionStage',
  'companyName',
  'addressLine1',
  'city',
  'state',
  'zipCode',
  'country',
  'filmmakerRole',
  'filmmakerName',
  'filmmakerEmail',
  'filmmakerPhone',
]

const MAX_DESCRIPTION_LENGTH = 2000

type Page2TorchProps = {
  data: TorchApplicationData
  onSaveData: (data: { name: string; value: string | string[] | boolean }) => void
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  handleSelectChange: (event: React.ChangeEvent<HTMLSelectElement>) => void
  handleInputBlur: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void
}

export const Page2Torch = ({
  data,
  handleInputChange,
  onSaveData,
  handleInputBlur,
  handleSelectChange,
}: Page2TorchProps) => {
  const { t } = useTranslate('guild-create')
  const { setPageStep, setPage, isDraft, type } = useTorchApplication()

  const errors = useMemo(() => {
    const errors = {} as Record<string, string>
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (data.filmmakerEmail && !emailRegex.test(data.filmmakerEmail)) {
      errors.filmmakerEmail = t('invalidEmail', 'Invalid email')
    }

    if (data.description && data.description.length > MAX_DESCRIPTION_LENGTH) {
      errors.description = t('charactersOverLimit', '{{ chars }} characters over limit', {
        chars: (data.description.length - MAX_DESCRIPTION_LENGTH).toLocaleString(),
      })
    }

    return errors
  }, [t, data])

  const requiredFieldsAnswered = requiredFields.every((field) => !!data[field as keyof TorchApplicationData])
  const genreAgesAnswered = data.genres?.includes(TorchApplicationGenre.Children) ? data.ages?.length : true

  const canProceed = requiredFieldsAnswered && genreAgesAnswered && !Object.keys(errors).length
  const disabled = !data?.torchAcknowledgement || !!data?.signatureDate || !isDraft
  const isFullFilm = type === TorchApplicationTypes.FullFilm

  return (
    <>
      <Page
        disabled={!canProceed}
        onBack={() => (isFullFilm ? setPageStep(0) : setPageStep(2))}
        onNext={() => setPage(3)}
      >
        <PageSection>
          <TitleMD weight="bold">{t('submissionDetails', 'Submission Details')}</TitleMD>
          <TorchAcknowledgement />
          <Input
            label={t('torchTitle', 'Torch Title')}
            value={data.title || ''}
            onChange={handleInputChange}
            name="title"
            disabled={disabled}
            required
            onBlur={handleInputBlur}
          />

          <TextArea
            label={t('torchDescription', 'Torch Description')}
            value={data.description || ''}
            name="description"
            onChange={handleInputChange}
            disabled={disabled}
            required
            onBlur={handleInputBlur}
            error={!!errors.description}
            helperText={
              errors.description || (MAX_DESCRIPTION_LENGTH - (data.description?.length || 0)).toLocaleString()
            }
            isValid={!errors.description}
          />
          <ProductionTypes
            disabled={disabled}
            value={data.productionType}
            onChange={onSaveData}
            name="productionType"
          />
          <PresentationTypes
            disabled={disabled}
            value={data.presentationType}
            onChange={onSaveData}
            name="presentationType"
          />
          <TorchGenres disabled={disabled} value={data.genres} onChange={onSaveData} name="genres" />
          {data?.genres?.includes(TorchApplicationGenre.Children) && (
            <TorchAges disabled={disabled} value={data.ages} onChange={onSaveData} name="ages" />
          )}

          <TorchProduction
            disabled={disabled}
            value={data.productionStage}
            name="productionStage"
            onChange={onSaveData}
          />
        </PageSection>

        <PageSection>
          <TitleMD weight="bold">{t('projectionCompanyDetails', 'Production Company Details')}</TitleMD>
          <Input
            label={t('companyName', 'Company Name')}
            value={data.companyName || ''}
            name="companyName"
            onChange={handleInputChange}
            disabled={disabled}
            required
            onBlur={handleInputBlur}
          />
          <TitleSM weight="bold">{t('address', 'Address')}</TitleSM>
          <Input
            label={t('addressLine1', 'Address Line 1')}
            value={data.addressLine1 || ''}
            name="addressLine1"
            onChange={handleInputChange}
            disabled={disabled}
            required
            onBlur={handleInputBlur}
          />
          <Input
            label={t('addressLine2', 'Address Line 2')}
            value={data.addressLine2 || ''}
            name="addressLine2"
            onChange={handleInputChange}
            disabled={disabled}
            onBlur={handleInputBlur}
          />
          <div className="flex flex-row gap-4">
            <Input
              containerClassName="flex-1"
              label={t('city', 'City')}
              value={data.city || ''}
              name="city"
              onChange={handleInputChange}
              disabled={disabled}
              required
              onBlur={handleInputBlur}
            />
            <Input
              containerClassName="flex-1"
              label={t('stateProvince', 'State/Province')}
              value={data.state || ''}
              name="state"
              onChange={handleInputChange}
              disabled={disabled}
              required
              onBlur={handleInputBlur}
            />
          </div>
          <div className="flex flex-row gap-4">
            <Input
              containerClassName="flex-1"
              label={t('postalZipCode', 'Postal / Zip Code')}
              value={data.zipCode || ''}
              name="zipCode"
              onChange={handleInputChange}
              disabled={disabled}
              required
              onBlur={handleInputBlur}
            />
            <Select
              containerClassName="flex-1"
              label={t('country', 'Country')}
              value={data.country || ''}
              name="country"
              disabled={disabled}
              onChange={handleSelectChange}
              placeholder={t('selectACountry', 'Select a country')}
              // this has to match Jotform country inputs exactly
              options={jotformCountries}
              required
            />
          </div>
        </PageSection>

        <PageSection>
          <TitleMD weight="bold">{t('yourInformation', 'Your Information')}</TitleMD>
          <Input
            label={t('roleWithinCompany', 'Role Within Company')}
            value={data.filmmakerRole || ''}
            onChange={handleInputChange}
            name="filmmakerRole"
            disabled={disabled}
            required
            onBlur={handleInputBlur}
          />
          <div className="flex flex-col gap-4 md:flex-row">
            <Input
              containerClassName="flex-1"
              label={t('yourFullName', 'Your Full Name')}
              onChange={handleInputChange}
              value={data.filmmakerName || ''}
              name="filmmakerName"
              disabled={disabled}
              required
              onBlur={handleInputBlur}
            />
            <Input
              containerClassName="flex-1"
              label={t('email', 'Email')}
              onChange={handleInputChange}
              value={data.filmmakerEmail || ''}
              name="filmmakerEmail"
              disabled={disabled}
              type="email"
              required
              onBlur={handleInputBlur}
              error={!!errors.filmmakerEmail}
              helperText={errors.filmmakerEmail}
              isValid={!errors.filmmakerEmail}
            />
            <Input
              containerClassName="flex-1"
              label={t('phoneNumber', 'Phone Number')}
              onChange={handleInputChange}
              value={data.filmmakerPhone || ''}
              name="filmmakerPhone"
              disabled={disabled}
              required
              onBlur={handleInputBlur}
              error={!!errors.filmmakerPhone}
              helperText={errors.filmmakerPhone}
              isValid={!errors.filmmakerPhone}
            />
          </div>
        </PageSection>

        <Button variant="oxide" disabled={!canProceed} onClick={() => setPage(3)} className="w-72">
          {t('continue', 'Continue')}
        </Button>
      </Page>
    </>
  )
}
